'use client';

import { FliptClient } from '@flipt-io/flipt-client-js';
import type {
  EvaluationContext,
  JsonValue,
  Logger,
  ResolutionDetails,
} from '@openfeature/web-sdk';

interface FliptOptions {
  url: string;
  authentication?: {
    jwtToken?: string;
  };
}

export class CustomFliptProvider {
  metadata = {
    name: 'CustomFliptProvider',
  };

  private client: FliptClient | null = null;
  private namespaceKey: string;
  private options: FliptOptions;
  private initialized = false;

  constructor(namespaceKey: string, options: FliptOptions) {
    this.namespaceKey = namespaceKey;
    this.options = options;
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    const clientOptions = {
      namespace: this.namespaceKey,
      url: this.options.url,
      ...(this.options.authentication?.jwtToken && {
        authentication: {
          jwtToken: this.options.authentication.jwtToken,
        },
      }),
    };

    this.client = await FliptClient.init(clientOptions);
    this.initialized = true;
  }

  resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean,
    context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<boolean> {
    if (!this.client) {
      return {
        value: defaultValue,
        reason: 'ERROR',
      };
    }

    try {
      const { targetingKey, ...fliptContext } = context;
      const entityId = targetingKey ? String(targetingKey) : 'anonymous';

      const result = this.client.evaluateBoolean({
        flagKey,
        entityId,
        context: fliptContext,
      });

      return {
        value: result.enabled ?? defaultValue,
        reason: result.reason || 'UNKNOWN',
      };
    } catch (error) {
      return {
        value: defaultValue,
        reason: 'ERROR',
        errorMessage: error instanceof Error ? error.message : String(error),
      };
    }
  }

  resolveStringEvaluation(
    flagKey: string,
    defaultValue: string,
    context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<string> {
    if (!this.client) {
      return {
        value: defaultValue,
        reason: 'ERROR',
      };
    }

    try {
      const { targetingKey, ...fliptContext } = context;
      const entityId = targetingKey ? String(targetingKey) : 'anonymous';

      const data = this.client.evaluateVariant({
        flagKey,
        entityId,
        context: fliptContext,
      });

      const resolutionResult: ResolutionDetails<string> & {
        variant?: string;
        flagMetadata?: { attachment: string; match: boolean };
      } = {
        value: data.variantKey || defaultValue,
        variant: data.variantKey,
        reason: data.reason || 'UNKNOWN',
        flagMetadata: {
          attachment: data.variantAttachment || '',
          match: data.match,
        },
      };

      return resolutionResult;
    } catch (error) {
      return {
        value: defaultValue,
        reason: 'ERROR',
        errorMessage: error instanceof Error ? error.message : String(error),
      };
    }
  }

  resolveNumberEvaluation(
    _flagKey: string,
    defaultValue: number,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<number> {
    return {
      value: defaultValue,
      reason: 'ERROR',
    };
  }

  resolveObjectEvaluation<T extends JsonValue>(
    _flagKey: string,
    defaultValue: T,
    _context: EvaluationContext,
    _logger: Logger,
  ): ResolutionDetails<T> {
    return {
      value: defaultValue,
      reason: 'ERROR',
    };
  }
}
